import { Inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    isDefined,
    NotificationIdentifier,
    NotificationInterface,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { CheckpointSelection } from '../types/checkpoint';
import { Checkpoints } from '../types/checkpoints';
import { ContentId } from '../types/content-block';
import { CustomerIdentification } from '../types/customer-identification';
import { DefectServicePartIdentification } from '../types/defect-service-part-identification';
import { isDefectiveComponentReplacement } from '../types/defective-component-replacement';
import { FormsConstants } from '../types/forms-constants';
import { FunctionalElement } from '../types/functional-element';
import { FunctionalElements } from '../types/functional-elements';
import { AstoriaOption, OPTION_FUNCTIONAL_ELEMENT_TYPE } from '../types/option';
import { RADIO_OPTION_TYPE } from '../types/radio-option';
import { RadioOptions } from '../types/radio-options';
import { AstoriaXmlUtilities } from '../utilities/astoria-xml-utilities';
import { CosimaXmlUtilities } from '../utilities/cosima-xml-utilities';
import { FormUtilities } from '../utilities/form-utilities';
import { PrologueReplacementService } from './prologue-replacement.service';

export type OptionState = AstoriaOptionState | CosimaOptionState;

export type AstoriaOptionState = { selected: AstoriaOption['selected']; xmlId: string };
export type CosimaOptionState = { id: ContentId; selectedChoiceXmlId: string | undefined };

function isAstoriaOptionState(state: OptionState): state is AstoriaOptionState {
    return isDefined((state as AstoriaOptionState).xmlId);
}

@Injectable({
    providedIn: 'root',
})
export class XmlSerializerService {
    private xmlDoc: XMLDocument | undefined;
    public constructor(
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly prologueReplacementService: PrologueReplacementService,
        private readonly translate: TranslateService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {}

    public setXml(doc: Document): void {
        this.xmlDoc = doc;
    }

    public unsetXml(): void {
        this.xmlDoc = undefined;
    }

    public replacePrologue(formId: string, type: FormsConstants.FORMTYPE, xml: string, prologue: string): string {
        // repl ace prologue for 832 and 834 forms
        return type !== FormsConstants.FORMTYPE.TRAININGCHECKLIST
            ? this.prologueReplacementService.process(formId, type, xml, prologue)
            : xml;
    }

    public updateInputField(id: string, newValue: string, type: FormsConstants.FORMTYPE): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateInputField: XML Doc is undefined`);
        }

        // Find and update value in document
        if (FormUtilities.isAstoriaForm(type)) {
            AstoriaXmlUtilities.setPdfFormByXmlId(this.xmlDoc, id, newValue);
        } else if (FormUtilities.isCosimaForm(type)) {
            CosimaXmlUtilities.updateInputField(this.xmlDoc, id, newValue);
        } else {
            throw new Error(`XmlParserService: updateInputField: unknown form type '${type}'`);
        }
    }

    public updateDefectiveServicePartInputField(id: string, newValue: string): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateDefectiveServicePartInputField: XML Doc is undefined`);
        }
        CosimaXmlUtilities.updateDefectServicePartInputField(this.xmlDoc, id, newValue);
    }

    public updateOption(state: OptionState, functionalElements: FunctionalElements): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateOption: XML Doc is undefined`);
        }
        if (isAstoriaOptionState(state)) {
            const selected = isDefined(state.selected)
                ? state.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.YES
                    ? RADIO_OPTION_TYPE.NO
                    : RADIO_OPTION_TYPE.YES
                : state.selected;

            this.updateCheckpoint(state.xmlId, selected);
        } else {
            const functionalElement = functionalElements[state.id];
            const xmlId = functionalElement.xmlId;
            if (!isDefined(xmlId)) {
                throw new Error(
                    `FormEffects: updateFunctionalElementSelection$: encountered functional element without XML id!`,
                );
            }
            this.updateFunctionalElementSelection(xmlId, state.selectedChoiceXmlId);
        }
    }

    public updateDependenciesForOption(
        state: OptionState,
        dependencies: {
            checkpointOptions: CheckpointSelection[];
            functionalElements: FunctionalElement[];
        },
        functionalElements: FunctionalElements,
    ): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateDependenciesForOption: XML Doc is undefined`);
        }

        if (isAstoriaOptionState(state)) {
            AstoriaXmlUtilities.updateOptionDependencies(this.xmlDoc, state.selected, dependencies);
        } else {
            const functionalElement = functionalElements[state.id];

            CosimaXmlUtilities.updateOptionDependencies(this.xmlDoc, state.id, functionalElement, dependencies);
        }
    }

    /**
     * Only used for Astoria, where checkpoints have their own dedicated XML structures
     *
     * @param id the XML id of the checkpoint
     * @param selected the selected value of the radio button
     * @throws Error if the XML document is undefined
     */
    public updateCheckpoint(id: string, selected: RADIO_OPTION_TYPE | undefined): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateCheckpoint: XML Doc is undefined`);
        }

        AstoriaXmlUtilities.setCheckbtnByXmlId(this.xmlDoc, id, selected);
    }

    /**
     * Only used for Cosima, where the yes/no/na radiogroup is embedded into each functional
     * element in the XML
     *
     * @param functionalElementXmlId the XML id of the functional element
     * @param choiceXmlId the XML id of the selected choice
     * @throws Error if the XML document is undefined
     */
    public updateFunctionalElementSelection(functionalElementXmlId: string, choiceXmlId: string | undefined): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateFunctionalElementSelection: XML Doc is undefined`);
        }

        CosimaXmlUtilities.updateFunctionalElementSelection(this.xmlDoc, functionalElementXmlId, choiceXmlId);
    }

    /**
     * Only used for Cosima, where the yes/no/na radiogroup is embedded into each functional
     * element in the XML
     *
     * @param elementXmlId the XML id of the functional element
     * @param visibility the visibility of the functional element
     * @throws Error if the XML document is undefined
     */
    public updateElementVisibility(elementXmlId: string | undefined, visibility: boolean): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateElementVisibility: XML Doc is undefined`);
        }

        CosimaXmlUtilities.updateElementVisibility(this.xmlDoc, elementXmlId, visibility);
    }

    public updateDropdownOptionSelectedAttribute(elementXmlId: string | undefined, selected: boolean): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: updateDropdownOptionSelectedAttribute: XML Doc is undefined`);
        }

        CosimaXmlUtilities.updateDropdownOptionSelection(this.xmlDoc, elementXmlId, selected);
    }

    public hideOptionPresentElements(): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: hideOptionPresentElements: XML Doc is undefined`);
        }

        CosimaXmlUtilities.hideOptionPresentElements(this.xmlDoc);
    }

    /**
     * Only used for Cosima,
     * The defective component replacement functional element is only available in Cosima
     *
     * @param functionalElement the functional element to check
     */
    public updateDefectiveComponentReplacementPopupFieldsVisibility(functionalElement: FunctionalElement): void {
        if (isDefectiveComponentReplacement(functionalElement)) {
            const swapInVisible =
                functionalElement.swapInComponent.serialNo.length > 0 ||
                functionalElement.swapInComponent.matNo.length > 0;

            const swapOutVisible =
                functionalElement.swapOutComponent.serialNo.length > 0 ||
                functionalElement.swapOutComponent.matNo.length > 0;

            const repairVisible = functionalElement.repairTime.length > 0;

            this.updateElementVisibility(functionalElement.swapInComponent.componentXmlId, swapInVisible);
            this.updateElementVisibility(functionalElement.swapOutComponent.componentXmlId, swapOutVisible);
            this.updateElementVisibility(functionalElement.repairTimeXmlId, repairVisible);
        }
    }

    public updateDefectServicePartIdentification(
        defectServicePartIdentification: DefectServicePartIdentification,
        materialKey: string,
    ): DefectServicePartIdentification {
        return {
            ...defectServicePartIdentification,
            materialNo: {
                ...defectServicePartIdentification.materialNo,
                value: materialKey,
            },
        };
    }

    public updateOrderNumberInCustomerIdentification(
        customerIdentification: CustomerIdentification,
        notificationId: NotificationIdentifier,
    ): CustomerIdentification {
        return {
            ...customerIdentification,
            orderNr: {
                ...customerIdentification.orderNr,
                value: notificationId.qmNum,
            },
        };
    }

    public updateCustomerIdentification(
        customerIdentification: CustomerIdentification,
        notificationId: NotificationIdentifier,
        type: FormsConstants.FORMTYPE,
    ): CustomerIdentification {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
        if (typeof notification === 'undefined') {
            throw new Error(
                `XmlParserService: updateCustomerIdentification: Could not load notification '${notificationId}'`,
            );
        }

        const mainEquipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof mainEquipment === 'undefined') {
            throw new Error(`XmlParserService: updateCustomerIdentification: main equipment is undefined!`);
        }

        const showFormCustomerEmailData = this.configService.getShowFormCustomerEmailData();

        const astoriaMapping: {
            [key in keyof Partial<CustomerIdentification>]: string;
        } = {
            customer1: notification.address.name1,
            customer2: notification.address.name2,
            address1: notification.address.street,
            address2: notification.address.postCode + ' ' + notification.address.city,
            communication1: showFormCustomerEmailData ? notification.address.fax1 : '',
            communication2: showFormCustomerEmailData ? notification.address.fax2 : '',
            department: notification.address.department,
            materialNr: mainEquipment.matnr,
            orderNr: notificationId.qmNum,
            mDate: moment().format(this.clientManager.getDateFormat(false)),
            room: notification.address.room,
            serialNr: mainEquipment.sernr,
            systemId: mainEquipment.tidnr,
        };

        const cosimaMapping: {
            [key in keyof Partial<CustomerIdentification>]: string;
        } = {
            customer1: notification.address.name1,
            customer2: notification.address.name2,
            address1: notification.address.street,
            address2: notification.address.postCode + ' ' + notification.address.city,
            department: notification.address.department,
            room: notification.address.room,
            materialNr: mainEquipment.matnr,
            serialNr: mainEquipment.sernr,
            orderNr: notificationId.qmNum,
            systemId: mainEquipment.tidnr,
            systemName: mainEquipment.equiShortDesc,
            functionalLocation: notification.functionalLocation,
        };

        const mapping = FormUtilities.isAstoriaForm(type) ? astoriaMapping : cosimaMapping;

        let updatedCustomerIdentification = { ...customerIdentification };
        Object.keys(customerIdentification).forEach((key) => {
            updatedCustomerIdentification = {
                ...updatedCustomerIdentification,
                [key]: {
                    ...updatedCustomerIdentification[key as keyof CustomerIdentification],
                    value: mapping[key as keyof CustomerIdentification],
                },
            };
        });

        return updatedCustomerIdentification;
    }

    public cleanMasterXml(submitMode: FormsConstants.SUBMIT_STATUS, type: FormsConstants.FORMTYPE): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: cleanMasterXml: xmlDoc is undefined!`);
        }

        const xmlUtilities = FormUtilities.isAstoriaForm(type) ? AstoriaXmlUtilities : CosimaXmlUtilities;

        if (
            submitMode === FormsConstants.SUBMIT_STATUS.FINAL ||
            submitMode === FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK
        ) {
            // Remo ve MODIFIED attribute only for final submissions; interims keep the attribute.
            xmlUtilities.removeModifiedAttribute(this.xmlDoc);
        }

        if (FormUtilities.isAstoriaForm(type)) {
            // Part s of the XML need to be removed so that they are also not rendered in the PDF generated from the XML
            AstoriaXmlUtilities.removeSignatureFromOptionFunctionalElement(this.xmlDoc);
            AstoriaXmlUtilities.removeSignatureFromWorkstepsPerformed(this.xmlDoc);
        }
    }

    public cleanParticipantXml(
        checkpoints: Checkpoints,
        type: FormsConstants.FORMTYPE,
        options: RadioOptions,
        functionalElements: FunctionalElements,
        fromDate?: moment.Moment,
        tillDate?: moment.Moment,
    ): void {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: cleanParticipantXml: xmlDoc is undefined!`);
        }

        if (FormUtilities.isAstoriaForm(type)) {
            // Remo ve content that was loaded from a previous interim file
            AstoriaXmlUtilities.removePreviousInterimContent(this.xmlDoc, checkpoints, options, functionalElements);

            // Remo ve checkpoints + functional elements outside given date range
            //
            // !!!  This functionality was removed from the scope of MARS2, but will probably be needed in the future.
            // !!!  Hence keeping the code in here for future use.
            if (
                typeof fromDate !== 'undefined' &&
                fromDate.isValid() &&
                typeof tillDate !== 'undefined' &&
                tillDate.isValid()
            ) {
                AstoriaXmlUtilities.removeByDate(this.xmlDoc, fromDate, tillDate);
            }

            // Remo ve checkpoints + functional elements answered with "No" or "NA"
            AstoriaXmlUtilities.removeByAnswer(this.xmlDoc, options);

            // Remo ve (now) empty sections to clean up XML structure
            AstoriaXmlUtilities.removeEmptySections(this.xmlDoc);

            // Remo ve MODIFIED attribute from the remaining PDFFORM elements
            AstoriaXmlUtilities.removeModifiedAttribute(this.xmlDoc);

            // Part s of the XML need to be removed so that they are also not rendered in the PDF generated from the XML
            AstoriaXmlUtilities.removeSignatureFromOptionFunctionalElement(this.xmlDoc);
            AstoriaXmlUtilities.removeSignatureFromWorkstepsPerformed(this.xmlDoc);
        }

        if (FormUtilities.isCosimaForm(type)) {
            CosimaXmlUtilities.removePreviousInterimContent(this.xmlDoc, functionalElements);
            // Remo ve functional elements outside given date range
            //
            // !!!  This functionality was removed from the scope of MARS2, but will probably be needed in the future.
            // !!!  Hence keeping the code in here for future use.
            if (
                typeof fromDate !== 'undefined' &&
                fromDate.isValid() &&
                typeof tillDate !== 'undefined' &&
                tillDate.isValid()
            ) {
                CosimaXmlUtilities.removeByDate(this.xmlDoc, fromDate, tillDate);
            }

            CosimaXmlUtilities.removeByAnswer(this.xmlDoc);
            CosimaXmlUtilities.removeModifiedAttribute(this.xmlDoc);
        }
    }

    public static fixBackslashesInEntities(xml: string): string {
        return xml.replace(/\\/g, '/');
    }
}
